import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackButtonClick, trackLead } from '../utils/tracking';

const {FiUser,FiMail,FiPhone,FiMapPin,FiCalendar,FiHeart}=FiIcons;

const QuotePage=()=> {
  const navigate=useNavigate();
  const [formData,setFormData]=useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    city: '',
    petName: '',
    petType: 'dog',
    petBreed: '',
    petAge: '',
    petGender: 'male',
    coverage: 'accident-illness',
    contactTime: 'anytime'
  });
  const [isSubmitting,setIsSubmitting]=useState(false);

  const californiaLocations=[
    'Los Angeles','San Francisco','San Diego','Sacramento','San Jose','Fresno','Oakland','Bakersfield','Anaheim','Santa Ana','Stockton','Riverside','Irvine','Chula Vista','Fremont','San Bernardino','Modesto','Fontana','Oxnard','Moreno Valley','Huntington Beach','Glendale','Santa Clarita','Garden Grove','Oceanside'
  ];

  const popularBreeds={
    dog: [
      'Labrador Retriever','Golden Retriever','German Shepherd','Bulldog','Poodle','Beagle','Rottweiler','Yorkshire Terrier','Dachshund','Siberian Husky','Mixed Breed','Other'
    ],
    cat: [
      'Domestic Shorthair','Domestic Longhair','Persian','Maine Coon','Ragdoll','British Shorthair','Siamese','Russian Blue','Bengal','Abyssinian','Mixed Breed','Other'
    ]
  };

  const handleInputChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({...prev,[name]: value}));
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setIsSubmitting(true);

    trackLead({
      content_name: 'Quote Request',
      value: 1, // Represents one lead
      currency: 'USD',
      ...formData,
    });

    // Simulate form submission
    await new Promise(resolve=> setTimeout(resolve,2000));

    // Store form data for thank you page
    localStorage.setItem('quoteFormData',JSON.stringify(formData));
    setIsSubmitting(false);
    navigate('/thank-you');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Instant Pet Insurance Quote
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete the form below to receive a personalized quote for Nationwide pet insurance. Our California specialists will contact you within 24 hours.
          </p>
        </motion.div>

        {/* Benefits Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-blue-600 text-white rounded-lg p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold">90%</div>
              <div className="text-blue-200">Maximum Reimbursement</div>
            </div>
            <div>
              <div className="text-2xl font-bold">$13</div>
              <div className="text-blue-200">Starting Monthly Cost</div>
            </div>
            <div>
              <div className="text-2xl font-bold">24/7</div>
              <div className="text-blue-200">Customer Support</div>
            </div>
          </div>
        </motion.div>

        {/* Quote Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
            <h2 className="text-2xl font-bold">Request Your Free Quote</h2>
            <p className="text-blue-100 mt-2">
              Fill out the information below to get started with your personalized quote
            </p>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Personal Information */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiUser} className="h-5 w-5 mr-2 text-blue-600" />
                Your Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="90210"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    California City *
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select your city</option>
                    {californiaLocations.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Pet Information */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiHeart} className="h-5 w-5 mr-2 text-blue-600" />
                Pet Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Name *
                  </label>
                  <input
                    type="text"
                    name="petName"
                    value={formData.petName}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your pet's name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Type *
                  </label>
                  <select
                    name="petType"
                    value={formData.petType}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="dog">Dog</option>
                    <option value="cat">Cat</option>
                    <option value="bird">Bird</option>
                    <option value="exotic">Exotic Pet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Breed *
                  </label>
                  <select
                    name="petBreed"
                    value={formData.petBreed}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select breed</option>
                    {popularBreeds[formData.petType]?.map((breed) => (
                      <option key={breed} value={breed}>
                        {breed}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Age *
                  </label>
                  <select
                    name="petAge"
                    value={formData.petAge}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select age</option>
                    <option value="puppy-kitten">Puppy/Kitten (Under 1 year)</option>
                    <option value="young">Young (1-3 years)</option>
                    <option value="adult">Adult (4-7 years)</option>
                    <option value="senior">Senior (8+ years)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pet Gender *
                  </label>
                  <select
                    name="petGender"
                    value={formData.petGender}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coverage Type *
                  </label>
                  <select
                    name="coverage"
                    value={formData.coverage}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="accident-only">Accident Only</option>
                    <option value="accident-illness">Accident & Illness</option>
                    <option value="complete">Complete Coverage (includes Wellness)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Preferences */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiCalendar} className="h-5 w-5 mr-2 text-blue-600" />
                Contact Preferences
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Best Time to Contact
                </label>
                <select
                  name="contactTime"
                  value={formData.contactTime}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="anytime">Anytime</option>
                  <option value="morning">Morning (9 AM - 12 PM)</option>
                  <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                  <option value="evening">Evening (5 PM - 8 PM)</option>
                </select>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 text-white px-12 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Processing...' : 'Get My Free Quote'}
              </button>
              <p className="mt-4 text-sm text-gray-600">
                By submitting this form,you agree to be contacted by Burbank Insurance Services regarding Nationwide pet insurance quotes. Your information is secure and will not be shared.
              </p>
            </div>
          </form>
        </motion.div>

        {/* Quick CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center bg-gray-100 rounded-lg p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need Coverage Immediately?
          </h3>
          <p className="text-gray-600 mb-6">
            Get instant quotes and start coverage today with Nationwide's online enrollment.
          </p>
          <a
            href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackButtonClick('Quote Page Quick CTA Start Coverage')}
            className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-block"
          >
            Start Coverage Now
          </a>
        </motion.div>
      </div>
    </div>
  );
};
export default QuotePage;